import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import KieApiKeyModal from './KieApiKeyModal';
import { generateKieVideo, checkKieVideoStatus } from '../services/kieApi';
import { initializeBucket } from '../services/supabaseStorage';

const {
  FiPlay, FiSettings, FiZap, FiVideo, FiMonitor, FiSmartphone, FiDownload,
  FiTrash2, FiAlertCircle, FiRefreshCw, FiCode, FiCheckCircle,
  FiShoppingBag, FiHeart, FiMail, FiFileText, FiShield, FiRocket,
  FiCpu, FiArchive, FiImage, FiX, FiSquare, FiFilm, FiClock, FiLink
} = FiIcons;

const MAX_STORED_VIDEOS = 10;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

const OtherModels = () => {
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('portrait');
  const [model, setModel] = useState('grok-imagine-text');
  const [mode, setMode] = useState('normal');
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState('1080p');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [enablePromptExpansion, setEnablePromptExpansion] = useState(true);
  const [seed, setSeed] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedVideos, setGeneratedVideos] = useState([]);
  const [showApiModal, setShowApiModal] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedDebug, setExpandedDebug] = useState(null);

  // Image upload states
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Video URL state for Runway Aleph
  const [videoUrl, setVideoUrl] = useState('');

  // Initialize Supabase bucket on mount
  useEffect(() => {
    const setupBucket = async () => {
      try {
        await initializeBucket();
      } catch (error) {
        console.error('Failed to initialize Supabase bucket:', error);
      }
    };
    setupBucket();
  }, []);

  // Get model capabilities
  const getModelCapabilities = () => {
    const capabilities = {
      'grok-imagine-text': {
        name: 'Grok Imagine (Text)',
        description: 'Text to Video - 6s max',
        aspectRatios: ['portrait', 'landscape', 'square'],
        supportsText: true,
        supportsImage: false,
        supportsVideoUrl: false,
        duration: 6,
        modes: ['fun', 'normal', 'spicy'],
        hasAdvancedOptions: false
      },
      'grok-imagine-image': {
        name: 'Grok Imagine (Image)',
        description: 'Image to Video - 6s max',
        aspectRatios: ['portrait', 'landscape', 'square'],
        supportsText: true,
        supportsImage: true,
        supportsVideoUrl: false,
        duration: 6,
        modes: ['fun', 'normal'],
        hasAdvancedOptions: false
      },
      'runway-aleph': {
        name: 'Runway Aleph',
        description: 'Video to Video - 5s max',
        aspectRatios: ['portrait', 'landscape', 'square'],
        supportsText: true,
        supportsImage: false,
        supportsVideoUrl: true,
        duration: 5,
        modes: ['normal'],
        hasAdvancedOptions: false
      },
      'wan-text': {
        name: 'Wan 2.5 (Text)',
        description: 'Text to Video - High Quality',
        aspectRatios: ['portrait', 'landscape', 'square'],
        supportsText: true,
        supportsImage: false,
        supportsVideoUrl: false,
        durations: [5, 10],
        resolutions: ['720p', '1080p'],
        modes: ['normal'],
        hasAdvancedOptions: true
      },
      'wan-image': {
        name: 'Wan 2.5 (Image)',
        description: 'Image to Video - High Quality',
        aspectRatios: [],
        supportsText: true,
        supportsImage: true,
        supportsVideoUrl: false,
        durations: [5, 10],
        resolutions: ['720p', '1080p'],
        modes: ['normal'],
        hasAdvancedOptions: true
      },
      'hailuo-pro': {
        name: 'Hailuo 2.3 Pro',
        description: 'Premium Image to Video',
        aspectRatios: [],
        supportsText: true,
        supportsImage: true,
        supportsVideoUrl: false,
        durations: [6, 10],
        resolutions: ['768P', '1080P'],
        modes: ['normal'],
        hasAdvancedOptions: false,
        requiresImage: true
      },
      'hailuo-standard': {
        name: 'Hailuo 2.3 Standard',
        description: 'Standard Image to Video',
        aspectRatios: [],
        supportsText: true,
        supportsImage: true,
        supportsVideoUrl: false,
        durations: [6, 10],
        resolutions: ['768P', '1080P'],
        modes: ['normal'],
        hasAdvancedOptions: false,
        requiresImage: true
      }
    };

    return capabilities[model] || capabilities['grok-imagine-text'];
  };

  const modelCaps = getModelCapabilities();

  useEffect(() => {
    const apiKey = localStorage.getItem('kie_api_key');
    setHasApiKey(!!apiKey);

    const savedVideos = localStorage.getItem('kie_generated_videos');
    if (savedVideos) {
      try {
        const videos = JSON.parse(savedVideos);
        setGeneratedVideos(videos);

        videos.forEach(video => {
          if ((video.status === 'processing' || video.status === 'pending') && video.taskId) {
            console.log('Resuming polling for task:', video.taskId);
            pollVideoStatus(video.taskId, video);
          }
        });
      } catch (e) {
        console.error('Error loading saved videos:', e);
      }
    }
  }, []);

  const handleSaveApiKey = (apiKey) => {
    localStorage.setItem('kie_api_key', apiKey);
    setHasApiKey(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please upload a valid image file (JPEG, PNG, WebP)');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setErrorMessage('Image size must be less than 10MB');
      return;
    }

    setUploadedImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setErrorMessage('');
  };

  const removeImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
  };

  const pollVideoStatus = async (taskId, tempVideo) => {
    const maxAttempts = 180;
    let attempts = 0;

    const poll = async () => {
      try {
        attempts++;
        const data = await checkKieVideoStatus(taskId, tempVideo.model);

        const status = data.status;
        const progress = data.progress || 0;

        setGeneratedVideos(prevVideos => {
          const updatedVideos = prevVideos.map(v =>
            v.id === tempVideo.id
              ? { ...v, progress, status, debugInfo: data._raw }
              : v
          );
          localStorage.setItem('kie_generated_videos', JSON.stringify(updatedVideos));
          return updatedVideos;
        });

        if (status === 'completed' || status === 'success') {
          const videoUrlResult = data.url;

          if (videoUrlResult) {
            const completedVideo = {
              ...tempVideo,
              url: videoUrlResult,
              status: 'completed',
              progress: 100,
              thumbnail: videoUrlResult,
              completedAt: new Date().toISOString(),
              debugInfo: data._raw
            };

            setGeneratedVideos(prevVideos => {
              const updatedVideos = prevVideos.map(v =>
                v.id === tempVideo.id ? completedVideo : v
              );
              localStorage.setItem('kie_generated_videos', JSON.stringify(updatedVideos));
              return updatedVideos;
            });
            return;
          }
        }

        if (status === 'failed' || status === 'error') {
          const failedVideo = {
            ...tempVideo,
            status: 'failed',
            progress: 0,
            error: data.error || 'Video generation failed',
            debugInfo: data._raw
          };

          setGeneratedVideos(prevVideos => {
            const updatedVideos = prevVideos.map(v =>
              v.id === tempVideo.id ? failedVideo : v
            );
            localStorage.setItem('kie_generated_videos', JSON.stringify(updatedVideos));
            return updatedVideos;
          });
          return;
        }

        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          setGeneratedVideos(prevVideos => {
            const updatedVideos = prevVideos.map(v =>
              v.id === tempVideo.id
                ? { ...v, status: 'timeout', error: 'Polling timed out' }
                : v
            );
            localStorage.setItem('kie_generated_videos', JSON.stringify(updatedVideos));
            return updatedVideos;
          });
        }
      } catch (error) {
        console.error('Error polling video status:', error);
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        }
      }
    };

    poll();
  };

  const handleGenerate = async () => {
    // Validation
    if (model === 'grok-imagine-image' && !uploadedImage) {
      setErrorMessage('Please upload an image for Image-to-Video mode');
      return;
    }

    if (model === 'wan-image' && !uploadedImage) {
      setErrorMessage('Please upload an image for Wan 2.5 Image-to-Video mode');
      return;
    }

    if ((model === 'hailuo-pro' || model === 'hailuo-standard') && !uploadedImage) {
      setErrorMessage('Please upload an image for Hailuo 2.3 models');
      return;
    }

    if (model === 'runway-aleph' && !videoUrl.trim()) {
      setErrorMessage('Please provide a video URL for Runway Aleph Video-to-Video mode');
      return;
    }

    if (model === 'runway-aleph' && videoUrl.trim()) {
      // Validate URL format
      try {
        new URL(videoUrl);
      } catch (e) {
        setErrorMessage('Please enter a valid video URL (must start with http:// or https://)');
        return;
      }
    }

    if (!prompt.trim()) {
      setErrorMessage('Please provide a text prompt');
      return;
    }

    if (!hasApiKey) {
      setShowApiModal(true);
      return;
    }

    setIsGenerating(true);
    setErrorMessage('');

    try {
      const response = await generateKieVideo({
        prompt,
        aspectRatio,
        model,
        image: uploadedImage,
        mode,
        videoUrl: model === 'runway-aleph' ? videoUrl.trim() : null,
        duration: modelCaps.durations ? duration : undefined,
        resolution: (modelCaps.hasAdvancedOptions || model === 'hailuo-pro' || model === 'hailuo-standard') ? resolution : undefined,
        negativePrompt: modelCaps.hasAdvancedOptions ? negativePrompt : undefined,
        enablePromptExpansion: modelCaps.hasAdvancedOptions ? enablePromptExpansion : undefined,
        seed: modelCaps.hasAdvancedOptions && seed ? seed : undefined
      });

      if (response && response.taskId) {
        const newVideo = {
          id: Date.now(),
          prompt: prompt || (videoUrl ? 'Video Transformation' : 'Video Generation'),
          aspectRatio,
          model,
          mode,
          taskId: response.taskId,
          status: response.status || 'processing',
          progress: 0,
          url: null,
          thumbnail: null,
          hasImage: !!uploadedImage,
          hasVideoUrl: !!videoUrl,
          duration: modelCaps.durations ? duration : modelCaps.duration,
          resolution: (modelCaps.hasAdvancedOptions || model === 'hailuo-pro' || model === 'hailuo-standard') ? resolution : undefined,
          createdAt: new Date().toISOString()
        };

        setGeneratedVideos(prevVideos => {
          const updatedVideos = [newVideo, ...prevVideos].slice(0, MAX_STORED_VIDEOS);
          localStorage.setItem('kie_generated_videos', JSON.stringify(updatedVideos));
          return updatedVideos;
        });

        pollVideoStatus(response.taskId, newVideo);

        // Clear form
        setPrompt('');
        setNegativePrompt('');
        setSeed('');
        setVideoUrl('');
        removeImage();
      } else {
        throw new Error('Invalid response from API');
      }
    } catch (error) {
      console.error('Error generating video:', error);
      setErrorMessage(error.message || 'Failed to generate video. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRetry = (video) => {
    if (video.taskId) {
      setGeneratedVideos(prevVideos => {
        const updatedVideos = prevVideos.map(v =>
          v.id === video.id
            ? { ...v, status: 'processing', progress: 0, error: null }
            : v
        );
        localStorage.setItem('kie_generated_videos', JSON.stringify(updatedVideos));
        return updatedVideos;
      });
      pollVideoStatus(video.taskId, video);
    }
  };

  const handleDownload = async (video) => {
    if (!video.url) return;

    try {
      const response = await fetch(video.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vidroid-${video.model}-${video.id}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download. Try right-clicking the video and "Save As".');
    }
  };

  const handleDelete = (videoId) => {
    setGeneratedVideos(prevVideos => {
      const updatedVideos = prevVideos.filter(v => v.id !== videoId);
      localStorage.setItem('kie_generated_videos', JSON.stringify(updatedVideos));
      return updatedVideos;
    });
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all saved videos? This action cannot be undone.')) {
      setGeneratedVideos([]);
      localStorage.removeItem('kie_generated_videos');
    }
  };

  const toggleDebug = (videoId) => {
    setExpandedDebug(expandedDebug === videoId ? null : videoId);
  };

  // Switch to appropriate mode when media is uploaded
  useEffect(() => {
    if (uploadedImage && model === 'grok-imagine-text') {
      setModel('grok-imagine-image');
    }
  }, [uploadedImage]);

  // Set default duration and resolution for Hailuo 2.3 models
  useEffect(() => {
    if (model === 'hailuo-pro' || model === 'hailuo-standard') {
      if (![6, 10].includes(duration)) {
        setDuration(6);
      }
      if (!['768P', '1080P'].includes(resolution)) {
        setResolution('768P');
      }
    }
  }, [model]);

  const getModelDisplayName = (modelId) => {
    const names = {
      'grok-imagine-text': 'Grok Imagine',
      'grok-imagine-image': 'Grok Imagine',
      'runway-aleph': 'Runway Aleph',
      'wan-text': 'Wan 2.5',
      'wan-image': 'Wan 2.5',
      'hailuo-pro': 'Hailuo 2.3 Pro',
      'hailuo-standard': 'Hailuo 2.3 Standard'
    };
    return names[modelId] || modelId;
  };

  return (
    <div className="min-h-screen flex flex-col pt-20">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-6">
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="relative"
              >
                <SafeIcon
                  icon={FiZap}
                  className="text-6xl text-purple-400 drop-shadow-[0_0_20px_rgba(168,85,247,0.7)]"
                />
                <div className="absolute inset-0 text-6xl text-purple-400 animate-pulse opacity-50">
                  <SafeIcon icon={FiZap} />
                </div>
              </motion.div>
            </div>

            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-6xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent mb-4"
            >
              Vidroid
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-xl text-gray-300 font-light"
            >
              AI-Powered Video Generation with Multiple Models
            </motion.p>

            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowApiModal(true)}
              className="mt-6 px-6 py-3 bg-gray-700/50 border border-purple-400/30 rounded-xl text-purple-400 hover:bg-gray-700 transition-all duration-300 flex items-center gap-2 mx-auto"
            >
              <SafeIcon icon={FiSettings} className="text-lg" />
              {hasApiKey ? 'Update Kie.AI API Key' : 'Configure Kie.AI API Key'}
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-8 border border-purple-400/20 shadow-[0_0_50px_rgba(168,85,247,0.1)]"
          >
            {/* MODEL SELECTOR */}
            <div className="mb-8">
              <label className="block text-purple-400 text-sm font-medium mb-4 flex items-center gap-2">
                <SafeIcon icon={FiSettings} className="text-lg" />
                Generation Mode
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { id: 'grok-imagine-text', name: 'Grok (Text)', description: 'Text to Video' },
                  { id: 'grok-imagine-image', name: 'Grok (Image)', description: 'Animate Image' },
                  { id: 'wan-text', name: 'Wan 2.5 (Text)', description: 'High Quality T2V' },
                  { id: 'wan-image', name: 'Wan 2.5 (Image)', description: 'High Quality I2V' },
                  { id: 'hailuo-pro', name: 'Hailuo 2.3 Pro', description: 'Premium I2V' },
                  { id: 'hailuo-standard', name: 'Hailuo 2.3 Std', description: 'Standard I2V' },
                  { id: 'runway-aleph', name: 'Runway Aleph', description: 'Transform Video' }
                ].map((modelOption) => (
                  <motion.button
                    key={modelOption.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setModel(modelOption.id)}
                    className={`p-3 rounded-xl border-2 transition-all duration-300 ${
                      model === modelOption.id
                        ? 'border-purple-400 bg-purple-400/10 shadow-[0_0_20px_rgba(168,85,247,0.3)]'
                        : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
                    }`}
                  >
                    <div className="text-left">
                      <div className="text-white font-medium text-sm">{modelOption.name}</div>
                      <div className="text-gray-400 text-xs">{modelOption.description}</div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* IMAGE UPLOAD (for image-to-video modes) */}
            {(model === 'grok-imagine-image' || model === 'wan-image' || model === 'hailuo-pro' || model === 'hailuo-standard') && (
              <div className="mb-8">
                <label className="block text-purple-400 text-sm font-medium mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiImage} className="text-lg" />
                  Upload Image (Required)
                </label>
                {!imagePreview ? (
                  <div className="border-2 border-dashed border-gray-600 rounded-xl p-6 text-center hover:border-purple-400 transition-colors duration-300">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer flex flex-col items-center gap-3"
                    >
                      <SafeIcon icon={FiImage} className="text-4xl text-gray-400" />
                      <div>
                        <p className="text-white font-medium">Click to upload image</p>
                        <p className="text-gray-400 text-sm mt-1">JPEG, PNG, WebP (max 10MB)</p>
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-xl"
                    />
                    <button
                      onClick={removeImage}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* VIDEO URL INPUT (for runway-aleph mode) */}
            {model === 'runway-aleph' && (
              <div className="mb-8">
                <label className="block text-purple-400 text-sm font-medium mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiLink} className="text-lg" />
                  Video URL (Required)
                </label>
                <div className="space-y-3">
                  <input
                    type="url"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://example.com/video.mp4"
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                  />
                  <div className="p-4 bg-blue-500/10 border border-blue-400/30 rounded-xl">
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiAlertCircle} className="text-blue-400 text-xl flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-300">
                        <p className="font-semibold mb-2">How to get a video URL:</p>
                        <ol className="list-decimal list-inside space-y-1 text-xs">
                          <li>Upload your video to a file hosting service (e.g., Google Drive, Dropbox, ImgBB)</li>
                          <li>Make the video publicly accessible</li>
                          <li>Copy the direct video file URL (must end with .mp4, .mov, or .webm)</li>
                          <li>Paste the URL above</li>
                        </ol>
                        <p className="mt-2 text-xs text-gray-400">
                          <strong>Note:</strong> The video must be accessible via a direct HTTPS link. First 5 seconds will be used.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PROMPT */}
            <div className="mb-8">
              <label className="block text-purple-400 text-sm font-medium mb-4 flex items-center gap-2">
                <SafeIcon icon={FiVideo} className="text-lg" />
                {model === 'runway-aleph'
                  ? 'Transformation Prompt (Required)'
                  : model === 'grok-imagine-image' || model === 'wan-image' || model === 'hailuo-pro' || model === 'hailuo-standard'
                  ? 'Motion Prompt (Required)'
                  : 'Video Prompt (Required)'}
              </label>
              <motion.div
                whileFocus={{ scale: 1.01 }}
                className="relative"
              >
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={
                    model === 'runway-aleph'
                      ? "Describe how you want to transform the video..."
                      : model === 'grok-imagine-image' || model === 'wan-image' || model === 'hailuo-pro' || model === 'hailuo-standard'
                      ? "Describe how the image should animate..."
                      : "Describe the video you want to generate..."
                  }
                  className="w-full h-32 bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 transition-all duration-300 resize-none"
                />
                <div className="absolute bottom-3 right-3 text-gray-400 text-sm">
                  {prompt.length}/{model === 'hailuo-pro' || model === 'hailuo-standard' ? '5000' : '800'}
                </div>
              </motion.div>
            </div>

            {/* DURATION & RESOLUTION (for Wan 2.5 and Hailuo 2.3 models) */}
            {(modelCaps.hasAdvancedOptions || model === 'hailuo-pro' || model === 'hailuo-standard') && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-purple-400 text-sm font-medium mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiClock} className="text-lg" />
                    Duration
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {modelCaps.durations.map((dur) => (
                      <motion.button
                        key={dur}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setDuration(dur)}
                        disabled={
                          model === 'hailuo-pro' || model === 'hailuo-standard'
                            ? (dur === 10 && resolution === '1080P')
                            : false
                        }
                        className={`py-3 px-4 rounded-lg border-2 transition-all duration-300 ${
                          duration === dur
                            ? 'border-purple-400 bg-purple-400/10 text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.3)]'
                            : (model === 'hailuo-pro' || model === 'hailuo-standard') && dur === 10 && resolution === '1080P'
                            ? 'border-gray-700 bg-gray-800/50 text-gray-600 cursor-not-allowed'
                            : 'border-gray-600 bg-gray-700/50 text-gray-300 hover:border-gray-500'
                        }`}
                      >
                        {dur}s
                      </motion.button>
                    ))}
                  </div>
                  {(model === 'hailuo-pro' || model === 'hailuo-standard') && (
                    <p className="text-gray-400 text-xs mt-2">
                      Note: 10s videos not supported for 1080P resolution
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-purple-400 text-sm font-medium mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiMonitor} className="text-lg" />
                    Resolution
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {modelCaps.resolutions.map((res) => (
                      <motion.button
                        key={res}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setResolution(res);
                          if ((model === 'hailuo-pro' || model === 'hailuo-standard') && res === '1080P' && duration === 10) {
                            setDuration(6);
                          }
                        }}
                        className={`py-3 px-4 rounded-lg border-2 transition-all duration-300 ${
                          resolution === res
                            ? 'border-purple-400 bg-purple-400/10 text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.3)]'
                            : 'border-gray-600 bg-gray-700/50 text-gray-300 hover:border-gray-500'
                        }`}
                      >
                        {res}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ASPECT RATIO (for models that support it) */}
            {modelCaps.aspectRatios && modelCaps.aspectRatios.length > 0 && (
              <div className="mb-8">
                <label className="block text-purple-400 text-sm font-medium mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiMonitor} className="text-lg" />
                  Aspect Ratio
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'portrait', name: 'Portrait', icon: FiSmartphone, ratio: model === 'runway-aleph' || model === 'wan-text' ? '9:16' : '2:3' },
                    { id: 'landscape', name: 'Landscape', icon: FiMonitor, ratio: model === 'runway-aleph' || model === 'wan-text' ? '16:9' : '3:2' },
                    { id: 'square', name: 'Square', icon: FiSquare, ratio: '1:1' }
                  ].map((ratio) => (
                    <motion.button
                      key={ratio.id}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setAspectRatio(ratio.id)}
                      className={`py-3 px-2 rounded-lg border-2 transition-all duration-300 flex flex-col items-center justify-center gap-1 ${
                        aspectRatio === ratio.id
                          ? 'border-purple-400 bg-purple-400/10 text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.3)]'
                          : 'border-gray-600 bg-gray-700/50 text-gray-300 hover:border-gray-500'
                      }`}
                    >
                      <SafeIcon icon={ratio.icon} className="text-lg" />
                      <span className="text-xs">{ratio.ratio}</span>
                    </motion.button>
                  ))}
                </div>
              </div>
            )}

            {/* GENERATION MODE (for Grok Imagine models) */}
            {!modelCaps.hasAdvancedOptions && modelCaps.modes.length > 1 && (
              <div className="mb-8">
                <label className="block text-purple-400 text-sm font-medium mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiSettings} className="text-lg" />
                  Generation Mode
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {modelCaps.modes.map((modeOption) => (
                    <motion.button
                      key={modeOption}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setMode(modeOption)}
                      className={`py-3 px-4 rounded-lg border-2 transition-all duration-300 capitalize ${
                        mode === modeOption
                          ? 'border-purple-400 bg-purple-400/10 text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.3)]'
                          : 'border-gray-600 bg-gray-700/50 text-gray-300 hover:border-gray-500'
                      }`}
                    >
                      {modeOption}
                    </motion.button>
                  ))}
                </div>
              </div>
            )}

            {/* ADVANCED OPTIONS (for Wan 2.5 models) */}
            {modelCaps.hasAdvancedOptions && (
              <div className="mb-8 space-y-6">
                <div>
                  <label className="block text-purple-400 text-sm font-medium mb-4">
                    Negative Prompt (Optional)
                  </label>
                  <input
                    type="text"
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    placeholder="Describe what to avoid (e.g., blurry, distorted)"
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                  />
                  <p className="text-gray-400 text-xs mt-2">Max 500 characters</p>
                </div>

                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enablePromptExpansion}
                      onChange={(e) => setEnablePromptExpansion(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-400"
                    />
                    <div>
                      <span className="text-white font-medium">Enable Prompt Enhancement</span>
                      <p className="text-gray-400 text-xs">AI will rewrite and improve your prompt for better results</p>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="block text-purple-400 text-sm font-medium mb-4">
                    Seed (Optional)
                  </label>
                  <input
                    type="number"
                    value={seed}
                    onChange={(e) => setSeed(e.target.value)}
                    placeholder="Random seed for reproducibility"
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                  />
                  <p className="text-gray-400 text-xs mt-2">Use the same seed to get consistent results</p>
                </div>
              </div>
            )}

            {/* INFO BOX */}
            <div className="mb-8 p-4 bg-blue-500/10 border border-blue-400/30 rounded-xl">
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiAlertCircle} className="text-blue-400 text-xl flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-300">
                  <p className="font-semibold mb-1">Model Info:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    {model === 'runway-aleph' ? (
                      <>
                        <li>Runway Aleph: Professional video-to-video editing</li>
                        <li>Maximum video duration: 5 seconds (input will be trimmed)</li>
                        <li>Requires publicly accessible video URL (not file upload)</li>
                        <li>Transform style, add/remove objects, relight scenes</li>
                        <li>Preserves motion and timing from source video</li>
                      </>
                    ) : model === 'wan-text' || model === 'wan-image' ? (
                      <>
                        <li>Wan 2.5: High-quality video generation by Alibaba</li>
                        <li>Duration: 5-10 seconds | Resolution: 720p or 1080p</li>
                        <li>Advanced prompt enhancement for better results</li>
                        <li>Supports negative prompts and seed control</li>
                        {model === 'wan-image' && <li>Animate images with natural motion</li>}
                      </>
                    ) : model === 'hailuo-pro' || model === 'hailuo-standard' ? (
                      <>
                        <li>Hailuo 2.3: Advanced image-to-video by MiniMax</li>
                        <li>Duration: 6 or 10 seconds | Resolution: 768P or 1080P</li>
                        <li>Pro version: Premium quality with enhanced detail</li>
                        <li>Standard version: Fast generation with good quality</li>
                        <li>Note: 10s videos not supported for 1080P resolution</li>
                        <li>Requires image upload (max 10MB)</li>
                      </>
                    ) : (
                      <>
                        <li>Maximum video duration: 6 seconds</li>
                        <li>Fun mode: Creative and playful results</li>
                        <li>Normal mode: Balanced and natural results</li>
                        {model === 'grok-imagine-text' && <li>Spicy mode: Bold and dramatic results</li>}
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* ERROR MESSAGE */}
            {errorMessage && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-500/10 border border-red-400/30 rounded-xl"
              >
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiAlertCircle} className="text-red-400 text-xl mt-0.5 flex-shrink-0" />
                  <p className="text-red-300 text-sm">{errorMessage}</p>
                </div>
              </motion.div>
            )}

            {/* GENERATE BUTTON */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGenerate}
              disabled={
                isGenerating ||
                (model === 'grok-imagine-image' && !uploadedImage) ||
                (model === 'wan-image' && !uploadedImage) ||
                (model === 'hailuo-pro' && !uploadedImage) ||
                (model === 'hailuo-standard' && !uploadedImage) ||
                (model === 'runway-aleph' && !videoUrl.trim())
              }
              className={`w-full py-4 rounded-xl font-medium text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                isGenerating ||
                (model === 'grok-imagine-image' && !uploadedImage) ||
                (model === 'wan-image' && !uploadedImage) ||
                (model === 'hailuo-pro' && !uploadedImage) ||
                (model === 'hailuo-standard' && !uploadedImage) ||
                (model === 'runway-aleph' && !videoUrl.trim())
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-400 hover:to-pink-500 shadow-[0_0_30px_rgba(168,85,247,0.3)] hover:shadow-[0_0_40px_rgba(168,85,247,0.5)]'
              }`}
            >
              {isGenerating ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  >
                    <SafeIcon icon={FiZap} className="text-xl" />
                  </motion.div>
                  {model === 'runway-aleph' ? 'Transforming Video...' : 'Generating Video...'}
                </>
              ) : (
                <>
                  <SafeIcon icon={FiPlay} className="text-xl" />
                  {model === 'runway-aleph'
                    ? `Transform Video (${modelCaps.duration}s)`
                    : modelCaps.durations
                    ? `Generate Video (${duration}s)`
                    : `Generate Video (${modelCaps.duration}s)`}
                </>
              )}
            </motion.button>
          </motion.div>

          {/* GENERATED VIDEOS LIST */}
          <AnimatePresence>
            {generatedVideos.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="mt-8"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <h2 className="text-2xl font-semibold text-purple-400">Generated Videos</h2>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <SafeIcon icon={FiArchive} className="text-base" />
                      <span>Last {MAX_STORED_VIDEOS} generations</span>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleClearAll}
                    className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-400/30 rounded-lg hover:bg-red-500/30 transition-colors duration-300 flex items-center gap-2"
                  >
                    <SafeIcon icon={FiTrash2} />
                    Clear All
                  </motion.button>
                </div>

                <div className="space-y-4">
                  {generatedVideos.map((video) => (
                    <motion.div
                      key={video.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-purple-400/20 shadow-[0_0_30px_rgba(168,85,247,0.1)]"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="md:col-span-1">
                          <div className="aspect-video bg-gray-800 rounded-lg overflow-hidden relative">
                            {video.status === 'completed' && video.url ? (
                              <video
                                src={video.url}
                                controls
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center p-4">
                                {(video.status === 'processing' || video.status === 'pending') && (
                                  <>
                                    <motion.div
                                      animate={{ rotate: 360 }}
                                      transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: "linear"
                                      }}
                                    >
                                      <SafeIcon icon={FiZap} className="text-4xl text-purple-400" />
                                    </motion.div>
                                    <p className="text-gray-400 text-sm mt-4 capitalize">{video.status}...</p>
                                    {video.progress !== undefined && (
                                      <div className="w-full mt-4">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-purple-400 text-xs font-medium">
                                            {video.progress}%
                                          </span>
                                        </div>
                                        <div className="w-full bg-gray-700 rounded-full h-2">
                                          <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${video.progress}%` }}
                                            transition={{ duration: 0.5 }}
                                            className="bg-gradient-to-r from-purple-400 to-pink-500 h-2 rounded-full"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                                {(video.status === 'failed' || video.status === 'error' || video.status === 'timeout') && (
                                  <>
                                    <SafeIcon icon={FiAlertCircle} className="text-4xl text-red-400" />
                                    <p className="text-red-400 text-sm mt-4 font-medium uppercase">{video.status}</p>
                                    {video.error && (
                                      <p className="text-red-300 text-xs mt-2 text-center">
                                        {video.error.length > 50 ? video.error.substring(0, 50) + '...' : video.error}
                                      </p>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="md:col-span-2 flex flex-col justify-between">
                          <div>
                            <div className="flex justify-between items-start">
                              <p className="text-white font-medium mb-2 line-clamp-2">{video.prompt}</p>
                              <button
                                onClick={() => toggleDebug(video.id)}
                                className="text-gray-500 hover:text-purple-400 transition-colors p-1"
                              >
                                <SafeIcon icon={FiCode} />
                              </button>
                            </div>

                            <div className="flex flex-wrap gap-2 mb-3">
                              <span className="px-3 py-1 bg-purple-400/10 text-purple-400 rounded-full text-xs">
                                {getModelDisplayName(video.model)}
                              </span>
                              <span className="px-3 py-1 bg-pink-400/10 text-pink-400 rounded-full text-xs">
                                {video.duration}s
                              </span>
                              {video.resolution && (
                                <span className="px-3 py-1 bg-cyan-400/10 text-cyan-400 rounded-full text-xs">
                                  {video.resolution}
                                </span>
                              )}
                              {video.hasImage && (
                                <span className="px-3 py-1 bg-blue-400/10 text-blue-400 rounded-full text-xs flex items-center gap-1">
                                  <SafeIcon icon={FiImage} className="text-[10px]" />
                                  With Image
                                </span>
                              )}
                              {video.hasVideoUrl && (
                                <span className="px-3 py-1 bg-green-400/10 text-green-400 rounded-full text-xs flex items-center gap-1">
                                  <SafeIcon icon={FiFilm} className="text-[10px]" />
                                  Video Transform
                                </span>
                              )}
                              <span
                                className={`px-3 py-1 rounded-full text-xs flex items-center gap-1 ${
                                  video.status === 'completed'
                                    ? 'bg-green-400/10 text-green-400'
                                    : video.status === 'processing' || video.status === 'pending'
                                    ? 'bg-yellow-400/10 text-yellow-400'
                                    : 'bg-red-400/10 text-red-400'
                                }`}
                              >
                                {video.status === 'completed' && <SafeIcon icon={FiCheckCircle} className="text-[10px]" />}
                                {video.status}
                              </span>
                            </div>

                            {expandedDebug === video.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                className="mb-3 p-3 bg-black/50 rounded-lg text-xs font-mono text-green-400 overflow-x-auto"
                              >
                                <p className="text-gray-400 mb-1">Task ID: {video.taskId}</p>
                                <p className="text-gray-400 mb-1">Status: {video.status}</p>
                                <p className="text-gray-400 mb-1">Model: {video.model}</p>
                                {video.debugInfo && <pre>{JSON.stringify(video.debugInfo, null, 2)}</pre>}
                              </motion.div>
                            )}

                            <p className="text-gray-400 text-xs">
                              Created: {new Date(video.createdAt).toLocaleString()}
                            </p>
                          </div>

                          <div className="flex gap-3 mt-4">
                            {video.status === 'completed' && (
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleDownload(video)}
                                className="flex-1 px-4 py-2 rounded-lg bg-purple-500 text-white hover:bg-purple-400 transition-colors duration-300 flex items-center justify-center gap-2"
                              >
                                <SafeIcon icon={FiDownload} />
                                Download
                              </motion.button>
                            )}
                            {(video.status === 'timeout' || video.status === 'error' || video.status === 'failed') && (
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleRetry(video)}
                                className="flex-1 px-4 py-2 rounded-lg bg-yellow-500/20 text-yellow-400 border border-yellow-400/30 hover:bg-yellow-500/30 transition-colors duration-300 flex items-center justify-center gap-2"
                              >
                                <SafeIcon icon={FiRefreshCw} />
                                Retry
                              </motion.button>
                            )}
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDelete(video.id)}
                              className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-400/30 rounded-lg hover:bg-red-500/30 transition-colors duration-300 flex items-center justify-center gap-2"
                            >
                              <SafeIcon icon={FiTrash2} />
                              Delete
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-12 mb-8 space-y-4">
            <motion.a
              href="https://app.base44.com/register?ref=S016XK7V824CI7V1"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.5 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full block"
            >
              <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 p-6 rounded-2xl border border-purple-400/30 shadow-[0_0_40px_rgba(168,85,247,0.3)] hover:shadow-[0_0_60px_rgba(168,85,247,0.5)] transition-all duration-300">
                <div className="flex items-center justify-center gap-3">
                  <SafeIcon icon={FiRocket} className="text-3xl text-white" />
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-white mb-1">
                      🚀 Built with Base44 - Create Your Own AI App Today!
                    </h3>
                    <p className="text-sm text-gray-200">
                      Click here to start building your own AI-powered application
                    </p>
                  </div>
                </div>
              </div>
            </motion.a>

            <motion.a
              href="https://greta.questera.ai/login?referralCode=i9xku45n"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full block"
            >
              <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 p-6 rounded-2xl border border-cyan-400/30 shadow-[0_0_40px_rgba(6,182,212,0.3)] hover:shadow-[0_0_60px_rgba(6,182,212,0.5)] transition-all duration-300">
                <div className="flex items-center justify-center gap-3">
                  <SafeIcon icon={FiCpu} className="text-3xl text-white" />
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-white mb-1">
                      ✨ Greta by Questera - AI Site Builder
                    </h3>
                    <p className="text-sm text-gray-200">
                      Build stunning websites with AI-powered tools
                    </p>
                  </div>
                </div>
              </div>
            </motion.a>
          </div>

          <KieApiKeyModal
            isOpen={showApiModal}
            onClose={() => setShowApiModal(false)}
            onSave={handleSaveApiKey}
            currentKey={localStorage.getItem('kie_api_key') || ''}
          />
        </div>
      </div>

      <footer className="relative z-10 mt-16 pb-8">
        <div className="w-full max-w-6xl mx-auto px-6">
          <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-8 border border-purple-400/20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="text-center md:text-left">
                <h3 className="text-purple-400 font-semibold mb-4 flex items-center justify-center md:justify-start gap-2">
                  <SafeIcon icon={FiShoppingBag} />
                  Shop
                </h3>
                <a
                  href="https://shop.dadda.cool/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg hover:from-purple-400 hover:to-pink-500 transition-all duration-300 shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] text-sm font-medium"
                >
                  <SafeIcon icon={FiShoppingBag} className="text-base" />
                  Merch
                </a>
              </div>

              <div className="text-center md:text-left">
                <h3 className="text-purple-400 font-semibold mb-4 flex items-center justify-center md:justify-start gap-2">
                  <SafeIcon icon={FiHeart} />
                  Support
                </h3>
                <a
                  href="https://buymeacoffee.com/daddacool"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-400 hover:to-purple-500 transition-all duration-300 shadow-[0_0_20px_rgba(236,72,153,0.3)] hover:shadow-[0_0_30px_rgba(236,72,153,0.5)] text-sm font-medium"
                >
                  <SafeIcon icon={FiHeart} className="text-base" />
                  Support Us
                </a>
              </div>

              <div className="text-center md:text-left">
                <h3 className="text-purple-400 font-semibold mb-4 flex items-center justify-center md:justify-start gap-2">
                  <SafeIcon icon={FiMail} />
                  Contact
                </h3>
                <a
                  href="https://fiverring.com/#/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-700 text-purple-400 border border-purple-400/30 rounded-lg hover:bg-gray-600 transition-all duration-300 text-sm font-medium"
                >
                  <SafeIcon icon={FiMail} className="text-base" />
                  Contact Us
                </a>
              </div>

              <div className="text-center md:text-left">
                <h3 className="text-purple-400 font-semibold mb-4 flex items-center justify-center md:justify-start gap-2">
                  <SafeIcon icon={FiFileText} />
                  Legal
                </h3>
                <div className="flex flex-col gap-2">
                  <a
                    href="/terms"
                    className="text-gray-300 hover:text-purple-400 transition-colors text-sm flex items-center justify-center md:justify-start gap-2"
                  >
                    <SafeIcon icon={FiFileText} className="text-xs" />
                    Terms of Service
                  </a>
                  <a
                    href="/privacy"
                    className="text-gray-300 hover:text-purple-400 transition-colors text-sm flex items-center justify-center md:justify-start gap-2"
                  >
                    <SafeIcon icon={FiShield} className="text-xs" />
                    Privacy Policy
                  </a>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-6">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-gray-400 text-sm text-center md:text-left">
                  © 2025 <span className="text-purple-400 font-semibold">@Ai8848</span> · All rights reserved.
                </p>
                <p className="text-gray-500 text-xs text-center md:text-right">
                  Email: <a href="mailto:fiverrings@gmail.com" className="text-purple-400 hover:text-purple-300 transition-colors">fiverrings@gmail.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OtherModels;